import i18next from 'i18next';

import { AppConfig } from './app-config';
import { IOSWebview } from './ios';
import { AndroidWebview, isAndroid } from './android';
import { OldWebviewEvents } from './old-webview-events';
import { registerAviataAppEvents } from './app-evaluate-events';
import { webviewLib } from './lib';
import type { WebviewAnalyticsEvent, WebviewRemoteJsonConfig } from './types';

export { isAndroid, AppConfig };
export type {
  WebviewAnalyticsEventParams,
  WebviewRemoteJsonConfig,
} from './types';

type ABTestsParams = {
  abTestKeys: string[];
  onLoadConfig: (jsonABTestsConfig?: WebviewRemoteJsonConfig) => unknown;
};

export const Webview = {
  init() {
    registerAviataAppEvents();
    this.requestAppConfig();
  },
  requestAppConfig() {
    IOSWebview.requestAppConfig();
    AndroidWebview.requestAppConfig();
  },
  requestABTestParameterValue({ abTestKeys, onLoadConfig }: ABTestsParams) {
    if (isAndroid()) {
      AndroidWebview.requestABTestParameterValue(abTestKeys);
    } else {
      IOSWebview.requestABTestParameterValue(abTestKeys);
    }

    window.AviataApp = {
      ...window.AviataApp,
      setABTestValue(jsonABTestsConfig?: WebviewRemoteJsonConfig) {
        onLoadConfig(jsonABTestsConfig);
      },
    };
  },
  /**
   * @description Method to open webview with provided link
   */
  openLink(link: string) {
    if (isAndroid()) {
      AndroidWebview.openLink(link);
    } else {
      IOSWebview.openLink(link);
    }
  },
  /**
   * @description Method to open browser with provided link
   */
  openLinkInBrowser(link: string) {
    if (isAndroid()) {
      AndroidWebview.openLinkInBrowser(link);
    } else {
      IOSWebview.openLinkInBrowser(link);
    }
  },
  goBackToMenu() {
    IOSWebview.goBackToMenu();
    AndroidWebview.goBackToMenu();
    OldWebviewEvents.goBackToMenu();
  },
  sendAnalyticsEvent({ name, params }: WebviewAnalyticsEvent) {
    /**
     * @description
     * В Android, когда вы отправляете параметры событий из вашего приложения,
     * они должны быть упакованы в объект с ключом "params".
     * Это связано с тем, что нативное (Java/Kotlin) приложение,
     * которое принимает эти параметры, ожидает получить все данные,
     * находящиеся внутри объекта "params".
     *
     * В iOS параметры отправляются непосредственно без необходимости
     * упаковывать их в объект
     */

    if (isAndroid()) {
      AndroidWebview.sendAnalyticsEvent({
        eventName: name,
        params: webviewLib.buildAnalyticsParams(params),
      });
    } else {
      IOSWebview.sendAnalyticsEvent({
        eventName: name,
        ...webviewLib.buildAnalyticsParams(params),
      });
    }
  },
  sendAnalyticEventToAmplitude({ eventName = '', params = {} }) {
    if (isAndroid()) {
      AndroidWebview.sendAnalyticEventToAmplitude({
        eventName,
        params: webviewLib.buildAnalyticsParams(params),
      });
    } else {
      IOSWebview.sendAnalyticEventToAmplitude({
        eventName,
        ...webviewLib.buildAnalyticsParams(params),
      });
    }
  },
  setAppLocale() {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (AppConfig.language) {
          const language = AppConfig.language.includes('kk') ? 'kk' : 'ru';
          i18next
            .changeLanguage(language)
            .then(() => {
              clearInterval(interval);
              resolve();
            })
            .catch(() => {
              this.requestAppConfig();
            });
        } else {
          this.requestAppConfig();
        }
      }, 1000);
    });
  },
  onCreated() {
    if (isAndroid()) {
      AndroidWebview.onCreated();
    } else {
      IOSWebview.onCreated();
    }
  },
};
